import React from "react";
import * as signalR from "@microsoft/signalr";
const isLocalMocksEnabled = import.meta.env.VITE_ENABLE_LOCAL_MOCKS === "true";

// 最後一個使用者離開後，延遲多久才真正斷線（吸收快速切換分頁的情況）
const STOP_DELAY = 3000;

const GATEWAP = {
	CHECKIN_ADD: "ReceiveCheckInMember",
	CHECKIN_UPDATE: "UpdateCheckInMember",
	RECEIVE_INBODY_PROCESS: "ReceiveInbodyProcessing",
	RECEIVE_INBODY_PDF_PATH: "ReceiveInBodyPdfPath",
	RECEIVE_INBODY_UNUSUAL_IMPEDANCE: "ReceiveUnusualImpedance",
	RECEIVE_INBODY_MEASURE_DAY: "ReceiveInBodyMeasureDay",
	RECEIVE_INBODY_MEASUREID: "ReceiveInBodyMeasureID",
};

// 模組層級單例：整個 App 共用同一條連線物件，避免同時開出多條平行連線。
let sharedConnection = null;
// 目前掛載中的使用者數量（引用計數）：歸零才真正斷線，有人 mount 就重連。
let consumerCount = 0;
// 延遲斷線的 timer 與進行中的 stop promise（處理斷線途中又重連的競態）
let stopTimer = null;
let stopPromise = null;
// 斷線重連成功後要執行的 callback（例如重新拉清單，補回斷線期間漏掉的事件）
const reconnectedCallbacks = new Set();

const getConnection = () => {
	if (!sharedConnection) {
		sharedConnection = new signalR.HubConnectionBuilder()
			.withUrl("/hubs")
			.withAutomaticReconnect() // 連線存活期間若斷線，自動重連
			.build();
		// onreconnected 只在建立連線時註冊一次，避免每次 mount 重複累加
		sharedConnection.onreconnected(() => {
			reconnectedCallbacks.forEach(cb => {
				try {
					cb();
				} catch (error) {
					console.error(error);
				}
			});
		});
	}
	return sharedConnection;
};

const useSignalr = () => {
	// 記錄「此元件」註冊過的 handler 與 reconnected callback，unmount 時只移除自己的，
	// 避免 remount 重複註冊造成事件被觸發多次，也讓 closure 能即時被釋放。
	const handlersRef = React.useRef([]);
	const reconnectedRef = React.useRef([]);
	// 確保同一個元件實例對 consumerCount 只 +1 / -1，不受 start/stop 被呼叫幾次影響
	const startedRef = React.useRef(false);

	const start = React.useCallback(
		async () => {
			if (isLocalMocksEnabled) return;
			// 有人重新進來：取消尚未執行的延遲斷線
			if (stopTimer) {
				clearTimeout(stopTimer);
				stopTimer = null;
			}
			const connection = getConnection();
			if (!startedRef.current) {
				startedRef.current = true;
				consumerCount += 1;
			}
			// 若正在斷線中，先等它結束再重新建立，避免 stop/start 互相打架
			if (stopPromise) {
				try {
					await stopPromise;
				} catch (error) {
					/* 忽略斷線過程的錯誤 */
				}
			}
			if (connection.state === signalR.HubConnectionState.Disconnected) {
				try {
					await connection.start();
				} catch (error) {
					console.error(error.toString());
				}
			}
		},
		// eslint-disable-next-line
		[]
	);

	const stop = React.useCallback(
		async () => {
			if (isLocalMocksEnabled) return;
			// 立即移除此元件註冊的 handler / callback（釋放 closure，避免重複觸發）
			if (sharedConnection) {
				handlersRef.current.forEach(([method, handler]) => sharedConnection.off(method, handler));
			}
			reconnectedRef.current.forEach(cb => reconnectedCallbacks.delete(cb));
			handlersRef.current = [];
			reconnectedRef.current = [];

			if (startedRef.current) {
				startedRef.current = false;
				consumerCount = Math.max(0, consumerCount - 1);
			}

			// 還有其他使用者掛載中就保留連線
			if (consumerCount > 0) return;

			// 沒有任何使用者了：延遲一小段時間再真正斷線，吸收快速切換分頁
			if (stopTimer) clearTimeout(stopTimer);
			stopTimer = setTimeout(() => {
				stopTimer = null;
				// 延遲期間又有人進來就不斷線
				if (consumerCount > 0) return;
				if (sharedConnection && sharedConnection.state !== signalR.HubConnectionState.Disconnected) {
					stopPromise = Promise.resolve(sharedConnection.stop()).finally(() => {
						stopPromise = null;
					});
				}
			}, STOP_DELAY);
		},
		// eslint-disable-next-line
		[]
	);

	//接收後台訊息
	const on = React.useCallback(
		async (type, fn) => {
			if (isLocalMocksEnabled) return;
			if (!GATEWAP[type]) {
				console.error(`No match type: ${type}`);
				return;
			}
			const connection = getConnection();
			const method = GATEWAP[type];
			const handler = args => {
				fn && fn(args);
			};
			connection.on(method, handler);
			handlersRef.current.push([method, handler]);
		},
		// eslint-disable-next-line
		[]
	);

	// 註冊斷線重連成功後要執行的 callback（例如重新拉取清單）
	const onReconnected = React.useCallback(
		fn => {
			if (isLocalMocksEnabled || typeof fn !== "function") return;
			reconnectedCallbacks.add(fn);
			reconnectedRef.current.push(fn);
		},
		// eslint-disable-next-line
		[]
	);

	const invoke = React.useCallback(
		async () => {
			if (isLocalMocksEnabled) return;
		},
		// eslint-disable-next-line
		[]
	);

	return { start, stop, on, onReconnected, invoke };
};

export default useSignalr;
